'use client';

import React from 'react';
import {
  TileFragmentFragment,
  TileFragmentNoGridFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { useExperiment } from '@/ui/providers/experiment-provider';
import { ExperimentAssignmentEffect } from '@/ui/components/experiment-assignment-effect/experiment-assignment-effect';
import { useTileVisibilityConditions } from '@/ui/providers/tile-visibility-conditions-provider';
import { filterTilesByCountry } from '@/utils/tile-country-visibility';
import { filterTilesByTiming } from '@/utils/tile-timing-visibility';
import {
  CONTENT_WRAPPER_SHAPE_ID,
  EXPERIMENT_CANDIDATES_COMPONENT_ID,
  EXPERIMENT_TRIGGER_COMPONENT_ID,
  EXPERIMENT_WRAPPER_TRIGGER_VALUE,
  ExperimentAssignment,
  ExperimentRequestState,
} from '@/utils/experiment-helper';
import { decideExperimentVariant, resolveControlArm } from '@/utils/tile-experiment-resolver';
import {
  TileVisibilityConditions,
  filterVisibleTiles,
} from '@/integrations/crystallize/helpers/crystallize-tiles-helper';
// Deliberate tile <-> renderer recursion (a container tile such as `split-layout` renders tiles again);
// safe because it is referenced only at render time, never at module init.
// eslint-disable-next-line import/no-cycle
import TileRenderer from '../tile-renderer/tile-renderer';

type AnyTile = TileFragmentFragment | TileFragmentNoGridFragment;

// Structural view of the `experimentWrapper` alias in __tile-fragments.gql: the content-wrapper
// shape holds one PIECE component, and the trigger selection and candidate relations sit inside its
// nested components. Declared locally, and read through a tolerant cast, so this file stays
// decoupled from the generated fragment types the way tile-country-visibility.ts does — and so a
// wrapper that predates the schema addition simply resolves to "no candidates" instead of blowing up.
interface ExperimentPieceComponentView {
  id?: string | null;
  content?: {
    __typename?: string;
    options?: { key: string; value: string }[] | null;
    items?: unknown[] | null;
  } | null;
}

interface ExperimentWrapperView {
  shape?: { identifier?: string | null } | null;
  experimentWrapper?: {
    content?: { __typename?: string; components?: ExperimentPieceComponentView[] | null } | null;
  } | null;
}

function isContentWrapper(tile: AnyTile): boolean {
  return tile.shape?.identifier === CONTENT_WRAPPER_SHAPE_ID;
}

function getPieceComponent(
  wrapper: ExperimentWrapperView,
  componentId: string,
): ExperimentPieceComponentView | undefined {
  const components = wrapper.experimentWrapper?.content?.components ?? [];

  return components.find((component) => component?.id === componentId);
}

// The trigger is what opts a wrapper into the experiment mechanism. An unset or unrecognised trigger
// value is treated as "not an experiment", which is what makes future trigger types additive.
function hasExperimentTrigger(wrapper: ExperimentWrapperView): boolean {
  const options =
    getPieceComponent(wrapper, EXPERIMENT_TRIGGER_COMPONENT_ID)?.content?.options ?? [];

  return options.some(
    (option) =>
      option?.value === EXPERIMENT_WRAPPER_TRIGGER_VALUE ||
      option?.key === EXPERIMENT_WRAPPER_TRIGGER_VALUE,
  );
}

function getWrapperCandidates(wrapper: ExperimentWrapperView): AnyTile[] {
  const items =
    getPieceComponent(wrapper, EXPERIMENT_CANDIDATES_COMPONENT_ID)?.content?.items ?? [];

  return items.filter((item): item is AnyTile => {
    const candidate = item as AnyTile | null;

    return candidate?.__typename === 'Document';
  });
}

export interface FlattenedExperimentTiles {
  tiles: AnyTile[];
  renderedAssignments: ExperimentAssignment[];
}

export interface ExperimentVisibilityContext {
  country: string;
  now: Date;
}

/**
 * Replaces every content-wrapper tile with the candidates of the arm this session should see.
 *
 * Single resolution point: every consumer of TilesArrayRenderer resolves here, so SSR, the flight
 * payload and hydration always agree. Candidates hidden in this country or outside their timing
 * window are dropped BEFORE the arms are formed: an arm must never be chosen — and recorded as an
 * exposure — for content the visitor cannot see, or conversions get credited to an invisible
 * variant while the control group saw real content.
 *
 * CONTENT CONSTRAINT that follows from this: an experiment name must address the same arm set
 * everywhere it is used. Because the surviving candidates decide the arm count, placing ONE
 * experiment on two pages whose candidates differ in country or timing tagging lets `% armCount`
 * resolve differently per page, and the session can flip arms mid-visit (the request state is
 * frozen per document, so a soft navigation still carries the assignment list the document
 * started with). One experiment per wrapper, with identical arms wherever it appears.
 */
export function flattenExperimentWrappers(
  tiles: AnyTile[],
  experiment: ExperimentRequestState,
  visibility: ExperimentVisibilityContext,
): FlattenedExperimentTiles {
  if (!tiles.some((tile) => isContentWrapper(tile))) {
    return { tiles, renderedAssignments: [] };
  }

  // Collected as an array: a page can hold several wrappers, and every one of their assignments
  // must be confirmed — a single slot would silently drop all but the last exposure.
  const renderedAssignments: ExperimentAssignment[] = [];

  const flattened = tiles.flatMap<AnyTile>((tile) => {
    if (!isContentWrapper(tile)) {
      return [tile];
    }

    const wrapper = tile as ExperimentWrapperView;
    const candidates = filterTilesByTiming(
      filterTilesByCountry(getWrapperCandidates(wrapper), visibility.country),
      visibility.now,
    );

    if (!hasExperimentTrigger(wrapper)) {
      // Not an experiment wrapper: fall back to the control group so the page keeps its content.
      return resolveControlArm(candidates);
    }

    const decision = decideExperimentVariant(candidates, experiment);
    const { renderedAssignment } = decision;

    if (
      renderedAssignment &&
      !renderedAssignments.some((entry) => entry.name === renderedAssignment.name)
    ) {
      renderedAssignments.push(renderedAssignment);
    }

    return decision.chosen;
  });

  return { tiles: flattened, renderedAssignments };
}

function TilesArrayRenderer({
  tiles,
  visibilityConditions,
}: Readonly<{
  tiles: TileFragmentFragment[] | TileFragmentNoGridFragment[];
  // The current surface's hide-<condition> inputs, normally supplied via the
  // TileVisibilityConditionsProvider context (so they also reach tiles nested in a container tile);
  // an explicit prop overrides the context. Only the order-confirmation page sets these today, but the
  // hide feature is generic — on any surface without them the condition filter is a no-op.
  visibilityConditions?: TileVisibilityConditions;
}>) {
  const { country } = useLocalization();
  const experiment = useExperiment();
  const contextVisibilityConditions = useTileVisibilityConditions();
  const resolvedVisibilityConditions = visibilityConditions ?? contextVisibilityConditions;
  // One timestamp per render. The page tree is request-rendered (the layout reads headers()), so
  // SSR and hydration run within the same request window and both the visibility filters and the
  // arm resolution see effectively the same clock; a Timing boundary landing inside that tiny gap
  // is the accepted residual risk.
  const now = new Date();

  // Resolve A/B wrappers first, so the tiles the filters below see are the ones actually chosen;
  // the wrapper candidates are pre-filtered for country/timing inside, so the arm choice only runs
  // over content this visitor can actually see.
  const { tiles: resolvedTiles, renderedAssignments } = flattenExperimentWrappers(
    tiles,
    experiment,
    { country, now },
  );

  // Hide tiles restricted to other countries, then tiles outside their scheduled "Timing" window,
  // then tiles whose `hide-<condition>` topic matches the passed conditions (all-false by default, so
  // a no-op unless a caller supplies them). Filtering before mapping removes the wrapper div entirely
  // so the page grid closes any visual gaps. This client check is defense-in-depth: it also covers
  // tiles rendered via Drawer, ProductLandingPage and ProductChooserWrapper, which use
  // TilesArrayRenderer without passing through getPageTilesContent.
  const tilesForCountry = filterTilesByCountry<TileFragmentFragment | TileFragmentNoGridFragment>(
    resolvedTiles,
    country,
  );
  const tilesForTiming = filterTilesByTiming(tilesForCountry, now);
  const visibleTiles = filterVisibleTiles(tilesForTiming, resolvedVisibilityConditions);

  return (
    <>
      {visibleTiles.map((tile) => {
        const tileDocument = tile.__typename === 'Document' ? tile : undefined;
        if (!tileDocument) {
          return <p key={`error-${tile.id}`}>Tile is of wrong type</p>;
        }

        return (
          <div
            key={tileDocument.id}
            data-tile-id={tileDocument.id}
            {...(tileDocument.externalReference
              ? { 'data-tile-alias': tileDocument.externalReference }
              : {})}
          >
            <TileRenderer tile={tileDocument} trackingPrefix={tileDocument.id} />
          </div>
        );
      })}
      {renderedAssignments.length > 0 && (
        <ExperimentAssignmentEffect assignments={renderedAssignments} />
      )}
    </>
  );
}

export default TilesArrayRenderer;
